import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Star, Send, Edit, AlertCircle } from 'lucide-react';
import { useToast } from '../../hooks/useToast';
import { useDomainQuery } from '../../hooks/useDomainQuery';
import { 
  createOrUpdateCourseReview, 
  getStudentCourseReview,
  CourseReview
} from '../../services/elearningReviewService';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Alert, AlertDescription } from '../ui/alert';

interface CourseReviewFormProps {
  courseId: string;
  studentId: string;
  enrollmentId: string;
  onReviewSubmitted?: () => void;
}

const CourseReviewForm: React.FC<CourseReviewFormProps> = ({
  courseId,
  studentId,
  enrollmentId,
  onReviewSubmitted
}) => {
  const { getFilterParams } = useDomainQuery();
  const { toast } = useToast();
  
  const [rating, setRating] = useState<number>(0);
  const [hoverRating, setHoverRating] = useState<number>(0);
  const [reviewText, setReviewText] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [existingReview, setExistingReview] = useState<CourseReview | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  
  useEffect(() => {
    console.log('CourseReviewForm - Props reçues:', { courseId, studentId, enrollmentId });
    
    if (courseId && studentId) {
      console.log('CourseReviewForm - Chargement de l\'avis existant');
      loadExistingReview();
    } else {
      console.log('CourseReviewForm - Données manquantes:', { 
        courseId, 
        studentId,
        enrollmentId
      });
    }
  }, [courseId, studentId]);
  
  const loadExistingReview = async () => {
    const { tenantId } = getFilterParams();
    if (!tenantId) return;
    
    setIsLoading(true);
    try {
      const review = await getStudentCourseReview(tenantId, courseId, studentId);
      if (review) {
        setExistingReview(review);
        setRating(review.rating);
        setReviewText(review.review_text || '');
      }
    } catch (error) {
      console.error('Erreur lors du chargement de l\'avis existant:', error);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const { tenantId, domain } = getFilterParams();
    if (!tenantId || rating === 0) {
      toast({
        title: 'Erreur',
        description: 'Veuillez attribuer une note avant de soumettre votre avis.',
        variant: 'destructive'
      });
      return;
    }
    
    setIsSubmitting(true);
    try {
      const result = await createOrUpdateCourseReview({
        tenant_id: tenantId,
        course_id: courseId,
        student_id: studentId,
        enrollment_id: enrollmentId,
        rating,
        review_text: reviewText.trim() || undefined,
        is_published: false // Les avis doivent être modérés avant publication
      });
      
      if (result) {
        toast({
          title: existingReview ? 'Avis mis à jour' : 'Avis soumis',
          description: existingReview 
            ? 'Votre avis a été mis à jour avec succès. Il sera visible après modération.' 
            : 'Votre avis a été soumis avec succès. Il sera visible après modération.',
          variant: 'default'
        });
        
        setExistingReview(result);
        
        // Notifier le parent que l'avis a été soumis
        if (onReviewSubmitted) {
          onReviewSubmitted();
        }
      } else {
        throw new Error('Échec de la soumission de l\'avis');
      }
    } catch (error) {
      console.error('Erreur lors de la soumission de l\'avis:', error);
      toast({
        title: 'Erreur',
        description: 'Une erreur est survenue lors de la soumission de votre avis.',
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  if (isLoading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-6 bg-gray-700/50 rounded w-1/3"></div>
        <div className="h-32 bg-gray-700/50 rounded"></div>
        <div className="h-10 bg-gray-700/50 rounded w-1/4"></div>
      </div>
    );
  }
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="mt-8"
    >
      <h3 className="text-xl font-bold mb-4 flex items-center">
        {existingReview ? (
          <>
            <Edit className="mr-2 text-blue-400" size={20} />
            Modifier votre avis
          </>
        ) : (
          <>
            <Star className="mr-2 text-yellow-400" size={20} />
            Donnez votre avis sur ce cours
          </>
        )}
      </h3>
      
      {existingReview && !existingReview.is_published && (
        <Alert className="mb-4 bg-blue-900/20 border-blue-800 text-blue-200">
          <AlertCircle className="h-4 w-4 text-blue-400" />
          <AlertDescription>
            Votre avis est en attente de modération et sera visible par les autres étudiants après approbation.
          </AlertDescription>
        </Alert>
      )}
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="flex flex-col items-center mb-6 bg-gray-800/30 p-4 rounded-lg">
          <div className="text-lg font-medium mb-2">Votre note</div>
          <div className="flex space-x-1">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                onClick={() => setRating(star)}
                onMouseEnter={() => setHoverRating(star)}
                onMouseLeave={() => setHoverRating(0)}
                className="p-1 transition-transform hover:scale-110 focus:outline-none"
              >
                <Star
                  size={32}
                  className={`
                    ${(hoverRating >= star || (!hoverRating && rating >= star))
                      ? 'text-yellow-400 fill-yellow-400'
                      : 'text-gray-400'}
                    transition-colors
                  `}
                />
              </button>
            ))}
          </div>
          {rating > 0 && (
            <div className="mt-2 text-sm text-white/70">
              {rating === 1 && 'Très déçu'}
              {rating === 2 && 'Déçu'}
              {rating === 3 && 'Correct'}
              {rating === 4 && 'Satisfait'}
              {rating === 5 && 'Très satisfait'}
            </div>
          )}
        </div>
        
        <div>
          <Textarea
            placeholder="Partagez votre expérience avec ce cours... (optionnel)"
            value={reviewText}
            onChange={(e) => setReviewText(e.target.value)}
            className="min-h-[120px] bg-gray-800/30 border-gray-700 text-white placeholder:text-white/40 focus:border-red-500"
          />
        </div>
        
        <div className="flex justify-end">
          <Button
            type="submit"
            disabled={isSubmitting || rating === 0}
            className="bg-red-600 hover:bg-red-700 disabled:bg-gray-700"
          >
            <Send className="mr-2" size={16} />
            {isSubmitting ? 'Envoi en cours...' : existingReview ? 'Mettre à jour' : 'Envoyer'}
          </Button>
        </div>
      </form>
    </motion.div>
  );
};

export default CourseReviewForm;
